import { useAuth } from "@clerk/react"
import type { RunLogDayDto, RunLogEntryDto } from "@repo/game-admin"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { AgGridReact } from "ag-grid-react"
import { Search } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader, Toolbar } from "@/components/layout"
import { Input } from "@/components/ui/input"
import { useOrpc } from "@/integrations/orpc"
import { RunDetailsDialog } from "./-RunDetailsDialog"
import type { RunRow } from "./-shared"
import { usePriceImportColumns } from "./-use-price-import-columns"

export const Route = createFileRoute("/tools/price-imports/")({
	component: PriceImportsPage,
	validateSearch: (search: Record<string, unknown>): { run?: string } => ({
		run: typeof search.run === "string" ? search.run : undefined,
	}),
})

function PriceImportsPage() {
	const { client, isAuthReady } = useOrpc()
	const { isSignedIn } = useAuth()
	const { run: runParam } = Route.useSearch()
	const navigate = Route.useNavigate()
	const [quickFilter, setQuickFilter] = React.useState("")
	const [details, setDetails] = React.useState<RunRow | null>(null)

	const query = useQuery({
		queryKey: ["catalogueQuality", "getPriceImportRunLog"],
		enabled: isAuthReady && !!isSignedIn,
		queryFn: () => client.catalogueQuality.getPriceImportRunLog({ days: 14 }),
	})

	const retry = useMutation({
		mutationFn: (run: RunRow) =>
			client.catalogueQuality.retryPriceImport(retryInput(run)),
		onSuccess: (res) => {
			toast.success(`Retry triggered (task ${res.taskId})`)
			setDetails(null)
		},
		onError: (err) => toast.error(`Retry failed: ${err.message}`),
	})

	const rows = React.useMemo<RunRow[]>(() => {
		const days = (query.data?.days ?? []) as unknown as RunLogDayDto[]
		const flat = days.flatMap((d) =>
			d.entries.map((e) => ({
				...e,
				date: d.date,
				runGroup: runGroupLabel({ ...e, date: d.date }),
			})),
		)
		const sizeByRun = new Map<string, number>()
		for (const r of flat) {
			sizeByRun.set(r.runGroup, (sizeByRun.get(r.runGroup) ?? 0) + 1)
		}
		return flat.map((r) => ({ ...r, runSize: sizeByRun.get(r.runGroup) ?? 1 }))
	}, [query.data])

	// Deep-link: open the run named by ?run=<runId> once its row has loaded.
	React.useEffect(() => {
		if (!runParam || details) return
		const match = rows.find((r) => r.runId === runParam)
		if (match) setDetails(match)
	}, [runParam, rows, details])

	const { columnDefs, autoGroupColumnDef } = usePriceImportColumns(retry)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Price imports"
				count={query.data ? rows.length : undefined}
				description="Nightly price-import runs of the last 14 days, per game and marketplace."
			/>
			<Toolbar>
				<div className="relative">
					<Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-meta" />
					<Input
						placeholder="Game, marketplace or date"
						value={quickFilter}
						onChange={(e) => setQuickFilter(e.target.value)}
						className="w-72 pl-8"
					/>
				</div>
			</Toolbar>
			<div className="min-h-0 flex-1">
				<AgGridReact<RunRow>
					rowData={rows}
					columnDefs={columnDefs}
					// Leaf columns non-sortable; the run log is ordered by the auto-group column.
					defaultColDef={{
						sortable: false,
						resizable: true,
						filter: false,
						suppressHeaderMenuButton: true,
					}}
					autoGroupColumnDef={autoGroupColumnDef}
					groupDefaultExpanded={0}
					// Single-day runs render as a flat row (no redundant run group).
					groupHideParentOfSingleChild="leafGroupsOnly"
					quickFilterText={quickFilter}
					loading={query.isLoading}
					onRowClicked={(e) => {
						// Skip when the click came from an action control (ag-grid's
						// onRowClicked ignores React's stopPropagation).
						const target = e.event?.target as HTMLElement | null
						if (target?.closest("[data-no-row-nav]")) return
						if (e.data) setDetails(e.data)
					}}
					rowClass="cursor-pointer"
					animateRows
				/>
			</div>

			<RunDetailsDialog
				run={details}
				onOpenChange={(open) => {
					if (!open) {
						setDetails(null)
						if (runParam) navigate({ search: {}, replace: true })
					}
				}}
				onRetry={(run) => retry.mutate(run)}
				retrying={retry.isPending}
			/>
		</div>
	)
}

/** Retry input: replay the captured trigger payload, else the row's price date. */
function retryInput(run: RunRow): {
	gameSlug: string
	startDate?: string
	endDate?: string
	productTypes?: string[]
} {
	if (run.payload?.gameSlug) {
		return {
			gameSlug: run.payload.gameSlug,
			...(run.payload.startDate && { startDate: run.payload.startDate }),
			...(run.payload.endDate && { endDate: run.payload.endDate }),
			...(run.payload.productTypes && {
				productTypes: run.payload.productTypes,
			}),
		}
	}
	return {
		gameSlug: run.gameSlug,
		...(run.priceDate && { startDate: run.priceDate }),
	}
}

/** Group key: game + marketplace + runId (or run date when runId is missing). */
function runGroupLabel(e: RunLogEntryDto & { date: string }): string {
	const runKey = e.runId ?? `legacy-${e.date}`
	return `${e.gameSlug}·${e.marketplace}·${runKey}`
}
